import React, { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom';
import Header from '../Header/header';
import Footer from '../Footer/footer';
import PurchaseList from '../../components/PurchaseCompanyList/purchaseList';
import { getAllCompanyPurchases } from '../../services/purchase/getCompanyPurchases';
import { getAllPurchaseCompanies } from '../../services/purchase/getCompanies';
import updatePublication from '../../services/purchase/updatePublication';
import { publishDisabledReason } from '../../utils/purchasePublication';
import { HOME_ROUTE } from '../../models/routes';
import cookie from '../../cookieHelper/cookie';
import { auth as AuthObject } from '../../services/login';
import './purchaseCompanyList.scss';

const PAGE_SIZE = 10;
const PORTALS = [ 'iimjobs', 'hirist' ];

export default function CompanyPurchases() {
    const { centralCompanyId } = useParams();
    const { state } = useLocation();
    const [ searchParams, setSearchParams ] = useSearchParams();
    const portal = PORTALS.includes(searchParams.get('portal')) ? searchParams.get('portal') : PORTALS[0];
    const navigationCompany = String(state?.company?.centralCompanyId) === centralCompanyId ? state.company : null;
    const [ fetchedCompany, setFetchedCompany ] = useState(null);
    const [ companyError, setCompanyError ] = useState('');
    const company = String(fetchedCompany?.centralCompanyId) === centralCompanyId ? fetchedCompany : navigationCompany;
    const companyName = company?.companyName || (companyError ? `Company ${centralCompanyId}` : 'Loading company…');
    const actionLock = useRef(false);
    const [ pendingId, setPendingId ] = useState('');
    const [ actionError, setActionError ] = useState('');
    const [ notice, setNotice ] = useState('');
    const [ page, setPage ] = useState(0);
    const [ attempt, setAttempt ] = useState(0);
    const [ data, setData ] = useState({ purchases: [], total: 0 });
    const [ loading, setLoading ] = useState(true);
    const [ error, setError ] = useState('');
    const [ sessionExpired, setSessionExpired ] = useState(false);

    useEffect(() => {
        const controller = new AbortController();
        if (!/^\d+$/.test(centralCompanyId) || !Number.isSafeInteger(Number(centralCompanyId)) || Number(centralCompanyId) <= 0) {
            setError('Invalid company ID. Please select a company from the company list.');
            setLoading(false);
            return () => controller.abort();
        }
        setLoading(true);
        setError('');
        getAllCompanyPurchases(centralCompanyId, controller.signal)
            .then(result => { if (!controller.signal.aborted) setData(result); })
            .catch(err => {
                if (controller.signal.aborted) return;
                const code = String(err.response?.data?.status?.code);
                if (err.response?.status === 401 || [ '10411', '10412', '10416', '10417' ].includes(code)) {
                    cookie.delete_cookie(process.env.REACT_APP_COOKIE_NAME);
                    cookie.delete_cookie('loginProfileInfo');
                    AuthObject.isAuthenticated = false;
                    setSessionExpired(true);
                    return;
                }
                setError('Unable to load purchases. Please try again.');
            })
            .finally(() => { if (!controller.signal.aborted) setLoading(false); });
        return () => controller.abort();
    }, [ centralCompanyId, attempt ]);

    useEffect(() => {
        if (navigationCompany?.companyName || !/^\d+$/.test(centralCompanyId)
            || !Number.isSafeInteger(Number(centralCompanyId)) || Number(centralCompanyId) <= 0) return undefined;
        const controller = new AbortController();
        setCompanyError('');
        getAllPurchaseCompanies(controller.signal)
            .then(result => {
                if (controller.signal.aborted) return;
                const match = result.companies.find(item => String(item.centralCompanyId) === centralCompanyId);
                if (!match?.companyName) {
                    setCompanyError('Company details could not be found.');
                    return;
                }
                setFetchedCompany(match);
            })
            .catch(err => {
                if (controller.signal.aborted) return;
                const code = String(err.response?.data?.status?.code);
                if (err.response?.status === 401 || [ '10411', '10412', '10416', '10417' ].includes(code)) {
                    cookie.delete_cookie(process.env.REACT_APP_COOKIE_NAME);
                    cookie.delete_cookie('loginProfileInfo');
                    AuthObject.isAuthenticated = false;
                    setSessionExpired(true);
                    return;
                }
                setCompanyError('Unable to load company details. Please try again.');
            });
        return () => controller.abort();
    }, [ centralCompanyId, navigationCompany?.companyName, attempt ]);

    const changePublication = async (purchase, action) => {
        if (actionLock.current) return;
        const reason = action === 'publish' ? publishDisabledReason(purchase, data.purchases) : '';
        if (reason) { setActionError(reason); return; }
        actionLock.current = true;
        setPendingId(purchase._id);
        setActionError('');
        setNotice('');
        try {
            const updated = await updatePublication(purchase._id, action);
            setData(current => ({
                ...current,
                purchases: current.purchases.map(item => item._id === updated._id ? {
                    ...item, ...updated,
                    activeStoryCount: updated.activeStories?.length ?? item.activeStoryCount,
                } : item),
            }));
            setNotice(action === 'publish' ? 'Purchase published successfully.' : `Purchase unpublished. Status: ${updated.statusLabel || (updated.status === 2 ? 'EXPIRED' : 'ACTIVE')}.`);
            // Refresh every page to reconcile concurrent updates and publication eligibility.
            setAttempt(value => value + 1);
        } catch (err) {
            const code = String(err.response?.data?.status?.code);
            if (err.response?.status === 401 || [ '10411', '10412', '10416', '10417' ].includes(code)) {
                cookie.delete_cookie(process.env.REACT_APP_COOKIE_NAME);
                cookie.delete_cookie('loginProfileInfo');
                AuthObject.isAuthenticated = false;
                setSessionExpired(true);
            } else {
                setActionError(err.response?.data?.status?.message || err.message || 'Unable to update purchase. Please try again.');
                setAttempt(value => value + 1);
            }
        } finally {
            actionLock.current = false;
            setPendingId('');
        }
    };

    if (sessionExpired) return <Navigate to="/" state={{ from: { pathname: `/companies/${centralCompanyId}/purchases` } }} replace />;

    const portalPurchases = data.purchases.filter(item => item.origin === portal);
    const totalPages = Math.max(1, Math.ceil(portalPurchases.length / PAGE_SIZE));
    const changePortal = value => {
        if (value === portal) return;
        setPage(0);
        setActionError('');
        setNotice('');
        const params = new URLSearchParams(searchParams);
        params.set('portal', value);
        setSearchParams(params, { replace: true, state });
    };
    return (
      <div className="bodyWrapper purchase-company-shell">
        <Header />
        <main className="purchase-company-page purchase-company-details">
          <Link className="purchase-back-link" to={{ pathname: HOME_ROUTE, search: typeof state?.companyListSearch === 'string' ? state.companyListSearch : '' }}>← Back to companies</Link>
          <div className="purchase-company-heading">
            <span className="purchase-company-eyebrow">COMPANY PURCHASES</span>
            <h1>{companyName}</h1>
            <div className="purchase-details-identifiers">
              <span><i className="fa fa-building-o" aria-hidden="true" />Central company ID <strong>{centralCompanyId}</strong></span>
              {company?.custId && <span><i className="fa fa-user-o" aria-hidden="true" />Customer ID <strong>{company.custId}</strong></span>}
            </div>
          </div>
          {companyError && <div role="alert" className="purchase-company-error">
            <p>{companyError}</p>
            <button type="button" onClick={() => setAttempt(value => value + 1)}>Retry</button>
          </div>}
          {actionError && <p role="alert" className="purchase-company-error">{actionError}</p>}
          {notice && <p role="status" className="purchase-action-notice">{notice}</p>}
          {loading && <p role="status">Loading purchases…</p>}
          {!loading && error && <div role="alert" className="purchase-company-error">
            <p>{error}</p>
            <button type="button" onClick={() => setAttempt(value => value + 1)}>Retry</button>
          </div>}
          {!loading && !error && <>
            <div className="purchase-portal-tabs" role="tablist" aria-label="Portal">
              {PORTALS.map(value => <button key={value} type="button" role="tab" id={`purchase-tab-${value}`}
                aria-selected={portal === value} aria-controls="purchase-portal-panel"
                className={portal === value ? 'is-active' : ''} disabled={Boolean(pendingId)} onClick={() => changePortal(value)}>
                {value} <span>{data.purchases.filter(item => item.origin === value).length}</span>
              </button>)}
            </div>
            <div className="purchase-company-card" role="tabpanel" id="purchase-portal-panel" aria-labelledby={`purchase-tab-${portal}`}>
              <div className="purchase-details-table-heading">
                <h2>Purchase records <span>{portalPurchases.length}</span></h2>
                <p>All dates shown in UTC</p>
              </div>
              <PurchaseList purchases={portalPurchases.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)} companyName={companyName}
                centralCompanyId={centralCompanyId} allPurchases={data.purchases} onPublicationChange={changePublication} pendingId={pendingId} /></div>
            <p className="purchase-company-total">{portalPurchases.length} {portalPurchases.length === 1 ? 'purchase' : 'purchases'} on {portal}</p>
          </>}
          {!error && (portalPurchases.length > PAGE_SIZE || page > 0) && <nav className="purchase-company-pagination" aria-label="Purchase pagination">
            <button type="button" disabled={loading || Boolean(pendingId) || page === 0} onClick={() => setPage(value => value - 1)}>Previous</button>
            <span>Page {page + 1} of {totalPages}</span>
            <button type="button" disabled={loading || Boolean(pendingId) || page + 1 >= totalPages} onClick={() => setPage(value => value + 1)}>Next</button>
          </nav>}
        </main>
        <Footer />
      </div>
    );
}
